import { request } from './api'

// Settings, Workflows, and the two settings it brought: the remediation
// deadlines per severity and whether a single invitation waits for a second
// person, tier by tier. The API draws every stage from the rule that runs it
// (apps/tprm-api/internal/api/workflows.go), so this file holds shapes only.
//
// Kept out of lib/api.ts so the settings it adds do not share lines with the
// screens other streams are changing in the same wave.

export type WorkflowStage = { key: string; label: string; sub?: string }

export type WorkflowVariant = { key: string; label?: string; stages: WorkflowStage[] }

// Who may do something: the capability the API checks, the act in words, and
// the jobs that hold it.
export type WorkflowAuthority = { capability: string; act: string; personas: string[] }

export type Workflow = {
  key: string
  name: string
  purpose: string
  variants: WorkflowVariant[]
  settings?: { label: string; value: string }[]
  configurable: string[]
  fixed?: string[]
  changedBy: WorkflowAuthority
  putInUseBy?: WorkflowAuthority
  source?: string
  editors: { href: string; label: string }[]
}

export type FindingDeadlines = {
  criticalDays: number
  highDays: number
  mediumDays: number
  lowDays: number
  configured: boolean
  updatedBy?: string
  updatedAt?: string
}

export type TierIssuingRule = { tier: number; label: string; rank: number; needsSecondPerson: boolean }

export type IssuingRules = {
  tiers: TierIssuingRule[]
  deploymentNeedsSecondPerson: boolean
  campaignNeedsSecondPerson: boolean
}

export function workflows(): Promise<{ workflows: Workflow[] }> {
  return request('/v1/settings/workflows')
}

export function findingDeadlines(): Promise<{ deadlines: FindingDeadlines; shipped: FindingDeadlines; note: string }> {
  return request('/v1/settings/finding-deadlines')
}

export function saveFindingDeadlines(d: {
  criticalDays: number
  highDays: number
  mediumDays: number
  lowDays: number
}): Promise<{ deadlines: FindingDeadlines; note: string }> {
  return request('/v1/settings/finding-deadlines', { method: 'PUT', body: JSON.stringify(d) })
}

export function issuingRules(): Promise<IssuingRules> {
  return request('/v1/settings/issuing')
}

export function saveTierIssuing(tier: number, needsSecondPerson: boolean): Promise<{ note: string }> {
  return request('/v1/settings/issuing/' + tier, { method: 'PUT', body: JSON.stringify({ needsSecondPerson }) })
}
